import React from 'react';

const SummaryDisplayFinancials = ({ summary }) => {
  const preprocessSection = (sectionString) => {
    // Handle both escaped and actual newlines
    sectionString = sectionString.replace(/\\n/g, '\n');
    
    // Split by lines and process
    const lines = sectionString.split('\n');
    const processedLines = [];
    let inBulletHeaderContext = false;
    let inStandaloneHeaderContext = false; // Track if we're after a standalone header
    
    for (let line of lines) {
      line = line.trim();
      
      // Skip empty lines
      if (line === '') continue;
      
      // Check if line has bold markers before removing them
      const hasBoldMarkers = line.includes('**');
      
      // Remove asterisks used for bold in markdown
      line = line.replace(/\*\*/g, '');
      
      // Main title (# header)
      if (line.startsWith('# ')) {
        let titleContent = line.substring(2);
        // Remove "Earnings Call" from title if present
        titleContent = titleContent.replace(/Earnings Call/gi, '').trim();
        processedLines.push({ type: 'main-title', content: titleContent });
        inBulletHeaderContext = false;
      }
      // Section headers (## headers)
      else if (line.startsWith('## ')) {
        processedLines.push({ type: 'section-header', content: line.substring(3) });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
      }
      // Subsection headers (### headers)
      else if (line.startsWith('### ')) {
        processedLines.push({ type: 'subsection-header', content: line.substring(4) });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
      }
      // Numbered lists with colon (like "1. **Leadership Risk**:" becomes a numbered header)
      else if (/^\d+\.\s/.test(line) && line.includes(':')) {
        const match = line.match(/^(\d+)\.\s(.+)$/);
        if (match) {
          const number = match[1];
          const content = match[2];
          // If it ends with a colon, treat as numbered-header
          if (content.trim().endsWith(':')) {
            processedLines.push({ type: 'numbered-header', number: number, content: content.trim() });
            inBulletHeaderContext = true;
          } else {
            // If colon is in the middle with content after, split it
            const colonIndex = content.indexOf(':');
            const header = content.substring(0, colonIndex + 1).trim();
            const text = content.substring(colonIndex + 1).trim();
            processedLines.push({ type: 'numbered-header', number: number, content: header });
            if (text) {
              processedLines.push({ type: 'sub-bullet', content: text });
            }
            inBulletHeaderContext = true;
          }
        }
      }
      // Regular numbered lists
      else if (/^\d+\.\s/.test(line)) {
        const match = line.match(/^(\d+)\.\s(.+)$/);
        if (match) {
          const number = match[1];
          const content = match[2];
          processedLines.push({ type: 'numbered-bullet', number: number, content: content });
          inBulletHeaderContext = false;
        }
      }
      // Bullet points with colon (like "- **Key Metrics**:" becomes a sub-header)
      else if (line.startsWith('- ') && line.includes(':')) {
        const content = line.substring(2);
        // If it ends with a colon and has content, treat as bullet-header
        if (content.trim().endsWith(':')) {
          processedLines.push({ type: 'bullet-header', content: content.trim(), bold: hasBoldMarkers });
          inBulletHeaderContext = true;
          inStandaloneHeaderContext = false;
        } else {
          // If colon is in the middle with content after, split it
          const colonIndex = content.indexOf(':');
          const header = content.substring(0, colonIndex + 1).trim();
          const text = content.substring(colonIndex + 1).trim();
          processedLines.push({ type: 'bullet-header', content: header, bold: hasBoldMarkers });
          if (text) {
            processedLines.push({ type: 'sub-bullet', content: text, bold: hasBoldMarkers, underStandaloneHeader: false });
          }
          inBulletHeaderContext = true;
          inStandaloneHeaderContext = false;
        }
      }
      // Regular bullet points - check if we're in a bullet-header context
      else if (line.startsWith('- ')) {
        if (inBulletHeaderContext) {
          processedLines.push({ type: 'sub-bullet', content: line.substring(2), bold: hasBoldMarkers, underStandaloneHeader: inStandaloneHeaderContext });
        } else {
          processedLines.push({ type: 'bullet', content: line.substring(2), bold: hasBoldMarkers });
        }
      }
      // Indented content (treat as sub-bullet or sub-sub-bullet based on indentation)
      else if (line.startsWith('    ') && processedLines.length > 0) {
        // 4+ spaces = sub-sub-bullet
        processedLines.push({ type: 'sub-sub-bullet', content: line.trim(), bold: hasBoldMarkers, underStandaloneHeader: inStandaloneHeaderContext });
      }
      else if (line.startsWith('  ') && processedLines.length > 0) {
        // 2 spaces = sub-bullet
        processedLines.push({ type: 'sub-bullet', content: line.trim(), bold: hasBoldMarkers, underStandaloneHeader: inStandaloneHeaderContext });
      }
      // Standalone bold headers with colon (like "**Revenue Performance:**")
      else if (hasBoldMarkers && line.includes(':') && line.trim().endsWith(':')) {
        processedLines.push({ type: 'standalone-header', content: line, bold: true });
        inBulletHeaderContext = true;
        inStandaloneHeaderContext = true;
      }
      // Horizontal rule
      else if (line === '---') {
        processedLines.push({ type: 'divider', content: '' });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
      }
      // Regular text
      else {
        processedLines.push({ type: 'text', content: line, bold: hasBoldMarkers });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
      }
    }
    
    return processedLines;
  };

  return (
    <div style={{ padding: '20px', background: '#ffffff', borderRadius: '8px', margin: '20px 0' }}>
      {summary.map((section, index) => {
        const processedLines = preprocessSection(section);
        return (
          <div key={index} style={{
              background: '#f9f9f9',
              padding: '20px',
              margin: '15px 0',
              borderRadius: '8px',
              borderLeft: '4px solid #007bff'
            }}>
            
            {processedLines.map((line, lineIndex) => {
              if (line.type === 'main-title') {
                return (
                  <h2 key={lineIndex} style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    marginBottom: '20px',
                    color: '#1a1a1a'
                  }}>
                    {line.content}
                  </h2>
                );
              } else if (line.type === 'section-header') {
                return (
                  <h3 key={lineIndex} style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginTop: '20px',
                    marginBottom: '10px',
                    color: '#2c2c2c',
                    borderBottom: '2px solid #007bff',
                    paddingBottom: '5px'
                  }}>
                    {line.content}
                  </h3>
                );
              } else if (line.type === 'subsection-header') {
                return (
                  <h4 key={lineIndex} style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginTop: '15px',
                    marginBottom: '8px',
                    color: '#444'
                  }}>
                    {line.content}
                  </h4>
                );
              } else if (line.type === 'numbered-header') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '20px',
                    marginTop: '12px',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    <span style={{ marginRight: '10px', color: '#007bff', minWidth: '25px' }}>{line.number}.</span>
                    <span style={{ flex: 1 }}>{line.content}</span>
                  </div>
                );
              } else if (line.type === 'numbered-bullet') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '20px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <span style={{ marginRight: '10px', color: '#007bff', minWidth: '25px' }}>{line.number}.</span>
                    <span style={{ flex: 1 }}>{line.content}</span>
                  </div>
                );
              } else if (line.type === 'standalone-header') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '0px',
                    marginTop: '15px',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    {line.content}
                  </div>
                );
              } else if (line.type === 'bullet-header') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '20px',
                    marginTop: '12px',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    fontWeight: line.bold ? '600' : 'normal',
                    color: '#333'
                  }}>
                    <span style={{ marginRight: '10px', color: '#007bff' }}>•</span>
                    <span style={{ flex: 1 }}>{line.content}</span>
                  </div>
                );
              } else if (line.type === 'sub-bullet') {
                // Use less indent if under standalone header, more if under bullet-header
                const leftMargin = line.underStandaloneHeader ? '20px' : '55px';
                // Blue bullets for 20px indent (same as regular bullets), grey for 45px
                const bulletColor = line.underStandaloneHeader ? '#007bff' : '#999';
                // Make blue bullets bigger
                const bulletSize = line.underStandaloneHeader ? '10px' : '8px';
                const bulletMarginTop = line.underStandaloneHeader ? '5px' : '6px';
                return (
                  <div key={lineIndex} style={{
                    marginLeft: leftMargin,
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    color: line.bold ? '#333' : '#555',
                    lineHeight: '1.6',
                    fontWeight: line.bold ? '600' : 'normal'
                  }}>
                    <span style={{ 
                      marginRight: '10px', 
                      color: bulletColor,
                      fontSize: bulletSize,
                      marginTop: bulletMarginTop
                    }}>●</span>
                    <span style={{ flex: 1 }}>{line.content}</span>
                  </div>
                );
              } else if (line.type === 'sub-sub-bullet') {
                // Use less indent if under standalone header, more if under bullet-header
                const leftMargin = line.underStandaloneHeader ? '45px' : '80px';
                // Grey bullets for deeper indentation
                const bulletColor = '#ccc';
                return (
                  <div key={lineIndex} style={{
                    marginLeft: leftMargin,
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    color: line.bold ? '#444' : '#666',
                    lineHeight: '1.5',
                    fontSize: '14px',
                    fontWeight: line.bold ? '600' : 'normal'
                  }}>
                    <span style={{ 
                      marginRight: '8px', 
                      color: bulletColor,
                      fontSize: '6px',
                      marginTop: '7px'
                    }}>●</span>
                    <span style={{ flex: 1 }}>{line.content}</span>
                  </div>
                );
              } else if (line.type === 'bullet') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '20px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <span style={{ marginRight: '10px', color: '#007bff' }}>•</span>
                    <span style={{ flex: 1 }}>{line.content}</span>
                  </div>
                );
              } else if (line.type === 'divider') {
                return (
                  <hr key={lineIndex} style={{
                    border: 'none',
                    borderTop: '1px solid #ddd',
                    margin: '20px 0'
                  }} />
                );
              } else if (line.type === 'text') {
                return (
                  <p key={lineIndex} style={{
                    marginBottom: '10px',
                    lineHeight: '1.6',
                    color: '#555',
                    fontWeight: line.bold ? '600' : 'normal'
                  }}>
                    {line.content}
                  </p>
                );
              }
              return null;
            })}
          </div>
        );
      })}
    </div>
  );
};

export default SummaryDisplayFinancials;